/**
 * useAnalytics Hook
 *
 * Custom hook for fetching and managing analytics data.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getAnalyticsSummary } from '../api/analyticsApi';

/**
 * Hook for managing analytics data
 *
 * @param {Object} filters - Filter parameters
 * @param {number} [filters.start_date] - Start date timestamp
 * @param {number} [filters.end_date] - End date timestamp
 * @param {string} [filters.location_id] - Location ID filter
 * @returns {Object} Analytics state and methods
 */
export const useAnalytics = (filters = {}) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Refetch analytics data manually
   */
  const refetch = useCallback(() => {
    setIsLoading(true);
    setError(null);

    getAnalyticsSummary(filters)
      .then((analyticsData) => {
        setData(analyticsData);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load analytics data');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filters]);

  // Fetch on mount and when filters change - SINGLE useEffect
  const fetchInProgressRef = useRef(false);
  const filtersStringRef = useRef('');

  useEffect(() => {
    const filtersString = JSON.stringify(filters);
    if (filtersStringRef.current === filtersString) {
      return;
    }
    filtersStringRef.current = filtersString;

    let cancelled = false;

    const fetchAnalytics = async () => {
      if (fetchInProgressRef.current) {
        return;
      }

      try {
        fetchInProgressRef.current = true;
        setIsLoading(true);
        setError(null);

        const analyticsData = await getAnalyticsSummary(filters);

        if (!cancelled) {
          setData(analyticsData);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load analytics data');
          setIsLoading(false);
        }
      } finally {
        fetchInProgressRef.current = false;
      }
    };

    fetchAnalytics();

    return () => {
      cancelled = true;
    };
  }, [filters]);

  return {
    data,
    isLoading,
    error,
    refetch
  };
};

export default useAnalytics;
